import { useEffect, useMemo, useState } from "react";
import {
  Link,
  ImageIcon,
  Bold,
  Italic,
  Underline,
  Paperclip,
  Table,
  AlignLeft,
  AlignRight,
  Loader2,
  Search,
  X,
  User as UserIcon,
  Mail,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import checkPermission from "../../utils/permission";
import { ApiService } from "../../services/api";
import { useModal } from "../../hooks/useModal";
import DepartmentSelector from "../../components/selectors/DepartmentSelector";
import UserSelector from "../../components/selectors/UserSelector";

export default function NewLessonPage() {
  const navigate = useNavigate();
  const { showAlert } = useModal();
  const hasAddPermission = checkPermission("lesson_add");
  const [loading, setLoading] = useState(false);
  const [lesson, setLesson] = useState({
    title: "",
    description: "",
    content: "",
    isDownloadable: false,
    isCertificate: false,
    isCommentable: false,
    isVotable: false,
    isQa: false,
    metadata: {
      isNewContentNotification: false,
      isCompletionNotification: false,
      isRemovedContentNotification: false,
    },
  });

  const [files, setFiles] = useState<any | null>(null);
  const [selectedUser, setSelectedUser] = useState<string>("");
  const [selectedUserDetails, setSelectedUserDetails] = useState<any>(null);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>([]);
  const [selectedDepartmentDetails, setSelectedDepartmentDetails] = useState<Record<string, any>>({});
  const [responsibleUsers, setResponsibleUsers] = useState<any[]>([]);

  const handleSave = async () => {
    if (!lesson.title.trim()) {
      await showAlert({
        title: "Hata",
        message: "Lütfen ders başlığını giriniz.",
        type: "error",
      });
      return;
    }

    if (!lesson.description.trim()) {
      await showAlert({
        title: "Hata",
        message: "Lütfen ders açıklamasını giriniz.",
        type: "error",
      });
      return;
    }

    if (!lesson.content.trim()) {
      await showAlert({
        title: "Hata",
        message: "Lütfen ders içeriğini giriniz. Bu alan zorunludur.",
        type: "error",
      });
      return;
    }
    const contentValue = lesson.content.trim();

    setLoading(true);
    try {
      const lessonData = {
        title: lesson.title.trim(),
        description: lesson.description.trim(),
        content: contentValue,
        isDownloadable: lesson.isDownloadable,
        isCertificate: lesson.isCertificate,
        isCommentable: lesson.isCommentable,
        isVotable: lesson.isVotable,
        isQa: lesson.isQa,
        metadata: {
          ...lesson.metadata,
        },
      };

      const res = await ApiService.createLesson(lessonData, files || undefined);
      if (res && res.id) {
        if (responsibleUsers.length > 0) {
          try {
            for (const responsible of responsibleUsers) {
              await ApiService.createLessonResponsible(res.id, {
                userId: responsible.userId,
                departmentIds: responsible.departmentIds,
              });
            }
          } catch (e) {
            console.warn("Sorumlu atama sırasında hata:", e);
          }
        }

        await showAlert({
          title: "Başarılı",
          message: `Ders başarıyla oluşturuldu. ${responsibleUsers.length > 0 ? `${responsibleUsers.length} sorumlu atandı.` : ""}`,
          type: "success",
        });

        navigate("/lessons");
      }
    } catch (err) {
      await showAlert({
        title: "Hata",
        message: "Ders oluşturulurken bir hata oluştu. Lütfen tekrar deneyin.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFiles(file);
  };

  const [activeContentTab, setActiveContentTab] = useState("Açıklama");

  const renderContentTabContent = () => {
    switch (activeContentTab) {
      case "Açıklama":
        return (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Detaylı İçerik <span className="text-red-500">*</span>
              </label>
              <div className="border border-gray-300 rounded-lg overflow-hidden">
                <div className="border-b border-gray-200 bg-gray-50 px-4 py-2">
                  <div className="flex items-center space-x-2">
                    <button className="p-2 hover:bg-gray-200 rounded transition-colors">
                      <Bold className="w-4 h-4 text-gray-600" />
                    </button>
                    <button className="p-2 hover:bg-gray-200 rounded transition-colors">
                      <Italic className="w-4 h-4 text-gray-600" />
                    </button>
                    <button className="p-2 hover:bg-gray-200 rounded transition-colors">
                      <Underline className="w-4 h-4 text-gray-600" />
                    </button>
                    <div className="w-px h-4 bg-gray-300 mx-2"></div>
                    <button className="p-2 hover:bg-gray-200 rounded transition-colors">
                      <ImageIcon className="w-4 h-4 text-gray-600" />
                    </button>
                    <button className="p-2 hover:bg-gray-200 rounded transition-colors">
                      <Paperclip className="w-4 h-4 text-gray-600" />
                    </button>
                    <button className="p-2 hover:bg-gray-200 rounded transition-colors">
                      <Link className="w-4 h-4 text-gray-600" />
                    </button>
                    <button className="p-2 hover:bg-gray-200 rounded transition-colors">
                      <Table className="w-4 h-4 text-gray-600" />
                    </button>
                    <div className="w-px h-4 bg-gray-300 mx-2"></div>
                    <button className="p-2 hover:bg-gray-200 rounded transition-colors">
                      <AlignLeft className="w-4 h-4 text-gray-600" />
                    </button>
                    <button className="p-2 hover:bg-gray-200 rounded transition-colors">
                      <AlignRight className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                </div>
                <div className="p-4">
                  <textarea
                    placeholder="Ders içeriğinizi buraya yazın... (Bu alan zorunludur)"
                    value={lesson.content}
                    onChange={(e) => setLesson({ ...lesson, content: e.target.value })}
                    className="w-full min-h-[350px] resize-none outline-none text-gray-700 leading-relaxed"
                  />
                </div>
              </div>
            </div>
          </div>
        );
      case "Sorumlular":
        return (
          <div className="space-y-6">
            <div className="space-y-6">
              <h3 className="text-lg font-semibold text-gray-900 border-b border-gray-200 pb-2">Sorumlular</h3>

              <div className="bg-white border border-blue-200 rounded-lg p-4">
                <h4 className="font-medium text-blue-900 mb-4">Yeni Sorumlu Ekle</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Kullanıcı Seçin <span className="text-red-500">*</span>
                    </label>
                    <div className="mb-3">
                      <UserSelector
                        value={selectedUser}
                        onChange={(userId, user) => {
                          setSelectedUser(userId);
                          setSelectedUserDetails(user);
                        }}
                        placeholder="Kullanıcı Seçin"
                        allowClear={true}
                        required={false}
                        inline={true}
                        autoOpen={true}
                        hideSelected={false}
                        className="text-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bağlantılı Departmanlar <span className="text-red-500">*</span>
                    </label>
                    <div className="mb-3">
                      <DepartmentSelector
                        value=""
                        onChange={(departmentId, department) => {
                          if (departmentId && department) {
                            if (!selectedDepartmentIds.includes(departmentId)) {
                              setSelectedDepartmentIds((prev) => [...prev, departmentId]);
                              setSelectedDepartmentDetails((prev) => ({
                                ...prev,
                                [departmentId]: department,
                              }));
                            }
                          }
                        }}
                        placeholder="Departman Seçin"
                        excludeIds={selectedDepartmentIds}
                        allowClear={false}
                        required={false}
                        showColors={true}
                        inline={true}
                        autoOpen={true}
                        className="text-sm"
                      />
                    </div>

                    {selectedDepartmentIds.length > 0 && (
                      <div className="mb-3 p-3 bg-blue-50 rounded-lg">
                        <div className="text-sm font-medium text-blue-900 mb-2">Seçili Departmanlar ({selectedDepartmentIds.length})</div>
                        <div className="flex flex-wrap gap-1">
                          {selectedDepartmentIds.map((deptId) => {
                            const dept = selectedDepartmentDetails[deptId];
                            return dept ? (
                              <span
                                key={deptId}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-xs">
                                {dept.color && <div className="w-3 h-3 rounded-full" style={{ backgroundColor: dept.color }} />}
                                {dept.name}
                                <button
                                  type="button"
                                  onClick={() => {
                                    setSelectedDepartmentIds((prev) => prev.filter((id) => id !== deptId));
                                    setSelectedDepartmentDetails((prev) => {
                                      const newDetails = { ...prev };
                                      delete newDetails[deptId];
                                      return newDetails;
                                    });
                                  }}
                                  className="ml-1 text-blue-600 hover:text-blue-800">
                                  ×
                                </button>
                              </span>
                            ) : null;
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-4">
                  <button
                    onClick={() => {
                      if (selectedUser && selectedDepartmentIds.length > 0) {
                        const newResponsible = {
                          userId: selectedUser,
                          departmentIds: selectedDepartmentIds,
                          user: {
                            id: selectedUser,
                            firstName: selectedUserDetails?.firstName || "Bilinmeyen",
                            lastName: selectedUserDetails?.lastName || "",
                            email: selectedUserDetails?.email || "Email bulunamadı",
                          },
                          departments: selectedDepartmentIds.map((id) => ({
                            id,
                            name: selectedDepartmentDetails[id]?.name || "Departman",
                            color: selectedDepartmentDetails[id]?.color,
                          })),
                        };

                        const existingIndex = responsibleUsers.findIndex((r: any) => r.userId === selectedUser);

                        if (existingIndex >= 0) {
                          setResponsibleUsers((prev) => {
                            const updated = [...prev];
                            const existingDeptIds = updated[existingIndex].departmentIds || [];
                            const newDeptIds = [...new Set([...existingDeptIds, ...selectedDepartmentIds])];

                            updated[existingIndex] = {
                              ...updated[existingIndex],
                              departmentIds: newDeptIds,
                              departments: newDeptIds.map((id) => ({
                                id,
                                name:
                                  selectedDepartmentDetails[id]?.name ||
                                  updated[existingIndex].departments?.find((d: any) => d.id === id)?.name ||
                                  "Departman",
                                color:
                                  selectedDepartmentDetails[id]?.color ||
                                  updated[existingIndex].departments?.find((d: any) => d.id === id)?.color,
                              })),
                            };
                            return updated;
                          });
                        } else {
                          setResponsibleUsers((prev) => [...prev, newResponsible]);
                        }

                        setSelectedUser("");
                        setSelectedUserDetails(null);
                        setSelectedDepartmentIds([]);
                        setSelectedDepartmentDetails({});
                      }
                    }}
                    disabled={!selectedUser || selectedDepartmentIds.length === 0}
                    className="bg-white px-4 py-2 rounded-md border border-gray-300 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed">
                    Sorumlu Ekle
                  </button>
                </div>
              </div>

              <div className="space-y-4">
                {responsibleUsers.length > 0 ? (
                  <div className="space-y-3">
                    {responsibleUsers.map((responsible: any, index) => (
                      <div key={index} className="bg-white rounded-lg p-4 border border-gray-200">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center mr-3 overflow-hidden">
                              {responsible.user?.media?.url ? (
                                <img
                                  src={responsible.user.media.url}
                                  alt={`${responsible.user.firstName} ${responsible.user.lastName}`}
                                  className="w-full h-full object-cover"
                                />
                              ) : (
                                <span className="text-white font-medium text-xs">
                                  {responsible.user?.firstName?.[0]}
                                  {responsible.user?.lastName?.[0]}
                                </span>
                              )}
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900">
                                {responsible.user?.firstName || "Bilinmeyen"} {responsible.user?.lastName || ""}
                              </h4>
                              <p className="text-sm text-gray-500">{responsible.user?.email || "Email bulunamadı"}</p>
                              <div className="flex flex-wrap gap-1 mt-2">
                                {responsible.departments &&
                                  responsible.departments.length > 0 &&
                                  responsible.departments.map((dept: any) => (
                                    <span key={dept.id} className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded">
                                      {dept.name || "Silinmiş Departman"}
                                    </span>
                                  ))}
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => {
                              setResponsibleUsers((prev) => prev.filter((_, i) => i !== index));
                            }}
                            className="text-red-600 hover:text-red-800 p-1">
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="bg-white rounded-lg p-6 border border-gray-200 text-center">
                    <p className="text-gray-500">Henüz sorumlu eklenmemiş.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      case "Seçenekler":
        return (
          <div className="space-y-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="space-y-6">
                <div className="bg-white border border-gray-200 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center">
                    <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center mr-3">
                      <span className="text-blue-600 text-sm">🔔</span>
                    </div>
                    Bildirim Ayarları
                  </h3>
                  <div className="space-y-4">
                    <div>
                      <span className="text-gray-800 text-xs font-medium mb-2 block">Yeni İçerik Bildirimi Gönderilsin mi?</span>
                      <div className="flex items-center space-x-4">
                        <label
                          className="flex items-center"
                          onClick={() =>
                            setLesson({
                              ...lesson,
                              metadata: {
                                ...lesson.metadata,
                                isNewContentNotification: true,
                              },
                            })
                          }>
                          <div
                            className={`w-3.5 h-3.5 rounded-full border-1.5 flex items-center justify-center mr-1.5 cursor-pointer ${
                              lesson.metadata.isNewContentNotification === true
                                ? "border-teal-500 bg-teal-500"
                                : "border-gray-500 bg-gray-500"
                            }`}>
                            <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                          </div>
                          <span className="text-gray-700 text-xs">Evet</span>
                        </label>
                        <label
                          className="flex items-center"
                          onClick={() =>
                            setLesson({
                              ...lesson,
                              metadata: {
                                ...lesson.metadata,
                                isNewContentNotification: false,
                              },
                            })
                          }>
                          <div
                            className={`w-3.5 h-3.5 rounded-full border-1.5 flex items-center justify-center mr-1.5 cursor-pointer ${
                              lesson.metadata.isNewContentNotification === false
                                ? "border-teal-500 bg-teal-500"
                                : "border-gray-500 bg-gray-500"
                            }`}>
                            <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                          </div>
                          <span className="text-gray-700 text-xs">Hayır</span>
                        </label>
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-800 text-xs font-medium mb-2 block">Tamamlama Bildirimi Gönderilsin mi?</span>
                      <div className="flex items-center space-x-4">
                        <label
                          className="flex items-center"
                          onClick={() =>
                            setLesson({
                              ...lesson,
                              metadata: {
                                ...lesson.metadata,
                                isCompletionNotification: true,
                              },
                            })
                          }>
                          <div
                            className={`w-3.5 h-3.5 rounded-full border-1.5 flex items-center justify-center mr-1.5 cursor-pointer ${
                              lesson.metadata.isCompletionNotification === true
                                ? "border-teal-500 bg-teal-500"
                                : "border-gray-500 bg-gray-500"
                            }`}>
                            <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                          </div>
                          <span className="text-gray-700 text-xs">Evet</span>
                        </label>
                        <label
                          className="flex items-center"
                          onClick={() =>
                            setLesson({
                              ...lesson,
                              metadata: {
                                ...lesson.metadata,
                                isCompletionNotification: false,
                              },
                            })
                          }>
                          <div
                            className={`w-3.5 h-3.5 rounded-full border-1.5 flex items-center justify-center mr-1.5 cursor-pointer ${
                              lesson.metadata.isCompletionNotification === false
                                ? "border-teal-500 bg-teal-500"
                                : "border-gray-500 bg-gray-500"
                            }`}>
                            <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                          </div>
                          <span className="text-gray-700 text-xs">Hayır</span>
                        </label>
                      </div>
                    </div>
                    <div>
                      <span className="text-gray-800 text-xs font-medium mb-2 block">Kaldırılmış İçerik Bildirimi Gönderilsin mi?</span>
                      <div className="flex items-center space-x-4">
                        <label
                          className="flex items-center"
                          onClick={() =>
                            setLesson({
                              ...lesson,
                              metadata: {
                                ...lesson.metadata,
                                isRemovedContentNotification: true,
                              },
                            })
                          }>
                          <div
                            className={`w-3.5 h-3.5 rounded-full border-1.5 flex items-center justify-center mr-1.5 cursor-pointer ${
                              lesson.metadata.isRemovedContentNotification === true
                                ? "border-teal-500 bg-teal-500"
                                : "border-gray-500 bg-gray-500"
                            }`}>
                            <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                          </div>
                          <span className="text-gray-700 text-xs">Evet</span>
                        </label>
                        <label
                          className="flex items-center"
                          onClick={() =>
                            setLesson({
                              ...lesson,
                              metadata: {
                                ...lesson.metadata,
                                isRemovedContentNotification: false,
                              },
                            })
                          }>
                          <div
                            className={`w-3.5 h-3.5 rounded-full border-1.5 flex items-center justify-center mr-1.5 cursor-pointer ${
                              lesson.metadata.isRemovedContentNotification === false
                                ? "border-teal-500 bg-teal-500"
                                : "border-gray-500 bg-gray-500"
                            }`}>
                            <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                          </div>
                          <span className="text-gray-700 text-xs">Hayır</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="space-y-8">
                <div>
                  <h2 className="text-xs font-bold text-gray-900 mb-4 pb-1 border-b border-gray-200">ERİŞİM YETKİLERİ</h2>
                  <div className="space-y-4">
                    <div className="space-y-3">
                      <div>
                        <span className="text-gray-800 text-xs font-medium mb-2 block">İndirilebilir mi?</span>
                        <div className="flex items-center space-x-4">
                          <label className="flex items-center" onClick={() => setLesson({ ...lesson, isDownloadable: true })}>
                            <div
                              className={`w-3.5 h-3.5 rounded-full border-1.5 flex items-center justify-center mr-1.5 cursor-pointer ${
                                lesson.isDownloadable === true ? "border-teal-500 bg-teal-500" : "border-gray-500 bg-gray-500"
                              }`}>
                              <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                            </div>
                            <span className="text-gray-700 text-xs">Evet</span>
                          </label>
                          <label className="flex items-center" onClick={() => setLesson({ ...lesson, isDownloadable: false })}>
                            <div
                              className={`w-3.5 h-3.5 rounded-full border-1.5 flex items-center justify-center mr-1.5 cursor-pointer ${
                                lesson.isDownloadable === false ? "border-teal-500 bg-teal-500" : "border-gray-500 bg-gray-500"
                              }`}>
                              <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                            </div>
                            <span className="text-gray-700 text-xs">Hayır</span>
                          </label>
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-800 text-xs font-medium mb-2 block">Yorum Yapılabilir mi?</span>
                        <div className="flex items-center space-x-4">
                          <label className="flex items-center" onClick={() => setLesson({ ...lesson, isCommentable: true })}>
                            <div
                              className={`w-3.5 h-3.5 rounded-full border-1.5 flex items-center justify-center mr-1.5 cursor-pointer ${
                                lesson.isCommentable === true ? "border-teal-500 bg-teal-500" : "border-gray-500 bg-gray-500"
                              }`}>
                              <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                            </div>
                            <span className="text-gray-700 text-xs">Evet</span>
                          </label>
                          <label className="flex items-center" onClick={() => setLesson({ ...lesson, isCommentable: false })}>
                            <div
                              className={`w-3.5 h-3.5 rounded-full border-1.5 flex items-center justify-center mr-1.5 cursor-pointer ${
                                lesson.isCommentable === false ? "border-teal-500 bg-teal-500" : "border-gray-500 bg-gray-500"
                              }`}>
                              <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                            </div>
                            <span className="text-gray-700 text-xs">Hayır</span>
                          </label>
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-800 text-xs font-medium mb-2 block">Oy Yapılabilir mi?</span>
                        <div className="flex items-center space-x-4">
                          <label className="flex items-center" onClick={() => setLesson({ ...lesson, isVotable: true })}>
                            <div
                              className={`w-3.5 h-3.5 rounded-full border-1.5 flex items-center justify-center mr-1.5 cursor-pointer ${
                                lesson.isVotable === true ? "border-teal-500 bg-teal-500" : "border-gray-500 bg-gray-500"
                              }`}>
                              <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                            </div>
                            <span className="text-gray-700 text-xs">Evet</span>
                          </label>
                          <label className="flex items-center" onClick={() => setLesson({ ...lesson, isVotable: false })}>
                            <div
                              className={`w-3.5 h-3.5 rounded-full border-1.5 flex items-center justify-center mr-1.5 cursor-pointer ${
                                lesson.isVotable === false ? "border-teal-500 bg-teal-500" : "border-gray-500 bg-gray-500"
                              }`}>
                              <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                            </div>
                            <span className="text-gray-700 text-xs">Hayır</span>
                          </label>
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-800 text-xs font-medium mb-2 block">Soru Sorulabilir mi?</span>
                        <div className="flex items-center space-x-4">
                          <label className="flex items-center" onClick={() => setLesson({ ...lesson, isQa: true })}>
                            <div
                              className={`w-3.5 h-3.5 rounded-full border-1.5 flex items-center justify-center mr-1.5 cursor-pointer ${
                                lesson.isQa === true ? "border-teal-500 bg-teal-500" : "border-gray-500 bg-gray-500"
                              }`}>
                              <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                            </div>
                            <span className="text-gray-700 text-xs">Evet</span>
                          </label>
                          <label className="flex items-center" onClick={() => setLesson({ ...lesson, isQa: false })}>
                            <div
                              className={`w-3.5 h-3.5 rounded-full border-1.5 flex items-center justify-center mr-1.5 cursor-pointer ${
                                lesson.isQa === false ? "border-teal-500 bg-teal-500" : "border-gray-500 bg-gray-500"
                              }`}>
                              <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                            </div>
                            <span className="text-gray-700 text-xs">Hayır</span>
                          </label>
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-800 text-xs font-medium mb-2 block">Sertifika Oluşturulacak mı?</span>
                        <div className="flex items-center space-x-4">
                          <label className="flex items-center" onClick={() => setLesson({ ...lesson, isCertificate: true })}>
                            <div
                              className={`w-3.5 h-3.5 rounded-full border-1.5 flex items-center justify-center mr-1.5 cursor-pointer ${
                                lesson.isCertificate === true ? "border-teal-500 bg-teal-500" : "border-gray-500 bg-gray-500"
                              }`}>
                              <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                            </div>
                            <span className="text-gray-700 text-xs">Evet</span>
                          </label>
                          <label className="flex items-center" onClick={() => setLesson({ ...lesson, isCertificate: false })}>
                            <div
                              className={`w-3.5 h-3.5 rounded-full border-1.5 flex items-center justify-center mr-1.5 cursor-pointer ${
                                lesson.isCertificate === false ? "border-teal-500 bg-teal-500" : "border-gray-500 bg-gray-500"
                              }`}>
                              <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                            </div>
                            <span className="text-gray-700 text-xs">Hayır</span>
                          </label>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  if (!hasAddPermission) {
    return (
      <div className="h-[88vh] bg-gray-50 flex items-center justify-center">
        <div className="bg-white rounded-xl shadow p-8 border border-gray-200 text-center max-w-md">
          <p className="text-gray-800 font-semibold mb-2">Bu sayfayı görüntüleme yetkiniz yok.</p>
          <p className="text-gray-600 mb-6">Gerekli yetki: lesson_add</p>
          <button onClick={() => navigate("/lessons")} className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700">
            Derslere Dön
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="h-[88vh] bg-gray-50 overflow-hidden flex flex-col">
      <div className="flex-1 overflow-hidden flex flex-col">
        <nav className="bg-white border-b border-gray-200 shadow-sm flex-shrink-0">
          <div className="px-6 py-3">
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2">
                <span className="font-semibold text-gray-900">E-Eğitim</span>
              </div>
              <div className="flex items-center gap-6 text-sm text-gray-600">
                <button
                  onClick={() => navigate("/lessons")}
                  className={`pb-3 transition-all duration-200 text-blue-600 font-medium border-b-2 border-blue-500`}>
                  Dersler
                </button>
                <button
                  onClick={() => navigate("/lessons?tab=homeworks")}
                  className={`pb-3 transition-all duration-200 hover:text-gray-900`}>
                  Ödevler
                </button>
                <button
                  onClick={() => navigate("/lessons?tab=deficiencies")}
                  className={`pb-3 transition-all duration-200 hover:text-gray-900`}>
                  Eksikler
                </button>
                <button onClick={() => navigate("/lessons?tab=reports")} className={`pb-3 transition-all duration-200 hover:text-gray-900`}>
                  Raporlar
                </button>
              </div>
            </div>
          </div>
        </nav>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 mb-8 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <h1 className="text-2xl font-bold mb-2">Yeni Ders Oluştur</h1>
                  <p className="text-indigo-100">Ders materyalinizi oluşturun ve katılımcıları belirleyin</p>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold text-sm hover:bg-green-700 disabled:opacity-60 disabled:cursor-not-allowed flex items-center gap-2"
                  onClick={handleSave}>
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Kaydediliyor...
                    </>
                  ) : (
                    "Kaydet"
                  )}
                </button>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
              <div className="p-6 border-b border-gray-200">
                <h2 className="text-xl font-semibold text-gray-900 mb-6">Ders Bilgileri</h2>
                <div className="flex items-start space-x-8">
                  <div className="w-40 h-40 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer group">
                    {files ? (
                      <img src={URL.createObjectURL(files)} alt="Ders Görseli" className="w-full h-full object-cover rounded-xl" />
                    ) : (
                      <div className="text-center" onClick={() => document.getElementById("fileInput")?.click()}>
                        <input id="fileInput" type="file" onChange={handleFileChange} className="hidden" />
                        <div className="w-12 h-12 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-3 group-hover:bg-indigo-200 transition-colors">
                          <ImageIcon className="w-6 h-6 text-indigo-600" />
                        </div>
                        <span className="text-sm text-gray-600 font-medium">Görsel Ekle</span>
                        <p className="text-xs text-gray-500 mt-1">PNG, JPG (Max 5MB)</p>
                      </div>
                    )}
                  </div>
                  <div className="flex-1 space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Ders Adı</label>
                      <input
                        type="text"
                        placeholder="Dersin adını giriniz"
                        value={lesson.title}
                        onChange={(e) => setLesson({ ...lesson, title: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Kısa Açıklama</label>
                      <textarea
                        rows={3}
                        placeholder="Ders hakkında kısa bir açıklama yazın"
                        value={lesson.description}
                        onChange={(e) => setLesson({ ...lesson, description: e.target.value })}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-colors resize-none"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <div className="border-b border-gray-200">
                <div className="px-6">
                  <div className="flex space-x-8">
                    {["Açıklama", "Sorumlular", "Seçenekler"].map((tab) => (
                      <button
                        key={tab}
                        onClick={() => setActiveContentTab(tab)}
                        className={`py-4 text-sm font-medium border-b-2 transition-colors ${
                          activeContentTab === tab
                            ? "border-indigo-500 text-indigo-600"
                            : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                        }`}>
                        {tab}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              <div className="p-6">{renderContentTabContent()}</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
